import StartCalling from 'DesktopCaller/components/StartCalling'
import Redialling from 'DesktopCaller/components/Redialling'
import PhoneNumber from 'DesktopCaller/components/PhoneNumber'
import HangUpReminder from 'DesktopCaller/components/HangUpReminder'
import TargetInfo from "Shared/components/TargetInfoNew"
import AudioCheck from 'DesktopCaller/components/Script/AudioCheck'
import Dialling from 'DesktopCaller/components/Script/Dialling'
import Outcome from 'DesktopCaller/components/Script/Outcome'
import InboundCall from 'DesktopCaller/components/Script/InboundCall'
import Questions from 'DesktopCaller/components/Script/Questions'
import Terminate from 'DesktopCaller/components/Script/Terminate'
import Rating from 'DesktopCaller/components/Script/Rating'
import Redial from 'DesktopCaller/components/Script/Redial'
import ScheduleCallback from 'DesktopCaller/components/Script/ScheduleCallback'
import Completed from 'DesktopCaller/components/Script/Completed'
import NoMoreCalls from 'DesktopCaller/components/Script/NoMoreCalls'
import LinesClosed from 'DesktopCaller/components/Script/LinesClosed'
import TargetList from 'DesktopCaller/components/TargetList'
import useData from 'DesktopCaller/useData'
import useAct from 'DesktopCaller/useAct'
import SanitizedHtml from 'Shared/components/SanitizedHtml'
import './TwilioCaller.scoped.scss'
import { useWindowSize } from 'usehooks-ts'
import { MOBILE_SCREEN_WIDTH } from 'Shared/constants'

export default function TwilioCaller({ clientCallOutcomes, condensedCaller, tabIndex }) {
  const size = useWindowSize()
  const act = useAct()
  const { callStage, inputStage, settings, lastOutcome, organisations } = useData((rs) => {
    const { callStage, inputStage, lastOutcome, organisations } = rs.callSession
    const settings = rs.campaign?.settings
    return { callStage, inputStage, settings, lastOutcome, organisations }
  })
  const onDemandCalling = useSel((s) => s.local.onDemandCalling)
  const [showHangUpReminder, setShowHangUpReminder] = useState(false)
  const [hangUpTimeout, setHangUpTimeout] = useState(null)

  const desktopSize = size.width >= MOBILE_SCREEN_WIDTH
  const showTargetInfo = desktopSize || tabIndex === 0
  const showScript = desktopSize || tabIndex === 1

  function cleanupTimeout() {
    if (hangUpTimeout) {
      clearTimeout(hangUpTimeout)
      setHangUpTimeout(null)
    }
  }

  useEffect(() => {
    if (callStage === 'connected_to_target') {
      setHangUpTimeout(
        setTimeout(() => {
          setShowHangUpReminder(true)
        }, (settings.maximumCallTime || 1800) * 1000)
      )
    } else {
      cleanupTimeout()
      setShowHangUpReminder(false)
    }
    return () => {
      cleanupTimeout()
    }
  }, [callStage])

  const { currentQuestionId, outcome, inbound } = useData((rs) => rs.call)
  const scriptTargetId = useSel((s) => s.local.currentScript?.targetId)

  const callingMode = useSel((s) => s.local.callingMode)
  const pusherSubscribed = useSel((s) => s.local.pusherSubscribed)

  const target = useData((rs) => rs.target)
  const campaignId = useData((rs) => rs.campaign.id)

  const scrollRef = useRef()

  function scroll() {
    if (scrollRef.current) {
      setTimeout(() => {
        if (!scrollRef.current) return
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, left: 0, behavior: 'smooth' })
      }, 250)
    }
  }
  useEffect(scroll, [currentQuestionId, inputStage, callStage, tabIndex])

  const callerSidebarMode = useData((rs) => rs.campaign?.settings?.callerSidebarMode)
  const customSidebarHtml = useSel((s) => s.local.customSidebarHtml)
  const customSidebarTargetId = useSel((s) => s.local.customSidebarTargetId)
  const hasCustomSidebar = callerSidebarMode === 'custom' && customSidebarHtml && customSidebarTargetId === target?.id

  useEffect(() => {
    if (target?.id && campaignId) {
      act.desktopCaller.fetchScript(campaignId, target.id)
      if (callerSidebarMode === 'custom') {
        act.local.upsert({ customSidebarHtml: null, customSidebarTargetId: null })
        act.desktopCaller.fetchSidebarContent(campaignId, target.id)
      }
    }
  }, [target?.id, campaignId, callerSidebarMode])

  function renderSidebarContent({ minimal = false, className = '' } = {}) {
    if (callerSidebarMode === 'custom') {
      if (hasCustomSidebar) {
        return <SanitizedHtml
          html={customSidebarHtml}
          preset='rich-text'
          className={`box custom-sidebar-content ${className}`.trim()}
        />
      }
      return null
    }
    if (target) {
      return minimal
        ? <div className="margin-bottom double"><TargetInfo target={target} minimal /></div>
        : <TargetInfo target={target} />
    }
    return null
  }

  const isCallAnswered = _.includes(['connected_to_target', 'finished_call'], callStage)
  const showTarget =
    scriptTargetId &&
    Number(target.id) === scriptTargetId &&
    ((_.includes(['ringing_target', 'connected_to_target'], callStage) &&
      inputStage !== 'failed') ||
      (callStage === 'finished_call' &&
        inputStage !== 'redial' &&
        inputStage !== 'failed'))

  const showQuestions =
    isCallAnswered &&
    _.includes(['answers', 'rate_call', 'redial'], inputStage) &&
    outcome === 'answer'
  const showScheduleCallback = inputStage === 'schedule_callback'
  const showTerminate =
    _.includes(['rate_call', 'redial'], inputStage) && callStage === 'connected_to_target'
  const showRating =
    inputStage === 'rate_call' && callStage === 'finished_call' && outcome !== 'no_answer'
  const showRedial =
    _.includes(['failed', 'redial', 'finished_call'], inputStage) &&
    callStage !== 'connected_to_target'
  const showCompleted = callStage === 'completed'
  const lastCallFailed = inputStage === 'failed'

  const headings = {
    no_answer: i18n.t('user.campaigns.phone.done_headings.no_answer'),
    left_message: i18n.t('user.campaigns.phone.done_headings.left_message'),
    bad_number: i18n.t('user.campaigns.phone.done_headings.bad_number'),
    number_not_recognised: i18n.t('user.campaigns.phone.done_headings.number_not_recognised'),
    busy: i18n.t('user.campaigns.phone.done_headings.busy'),
  }

  function getPreContent() {
    if (showCompleted) return <Completed />
    if (callStage === 'no_more_calls') return <NoMoreCalls organisation={organisations ? organisations[0] : null} />
    if (callStage === 'lines_closed') return <LinesClosed />
    if (callStage === 'confirm_audio') return <AudioCheck />
    if (showRedial) {
      if (onDemandCalling) {
        return <TargetList showDisconnect={true} heading={headings[lastOutcome] || i18n.t('user.campaigns.phone.call_completed', { defaultValue: 'Call completed' })} />
      }
      return <Redial failed={lastCallFailed} />
    }
    if (!showRedial) {
      if (onDemandCalling) {
        return <TargetList />
      }
      return <Redialling />
    }
  }

  function blocks() {
    return <div className="blocks">
      <Dialling />
      {!desktopSize && target?.id && renderSidebarContent({ minimal: true, className: 'margin-bottom double' })}
      {isCallAnswered && !inbound && (
        <Outcome key="outcome" clientCallOutcomes={clientCallOutcomes} />
      )}
      {isCallAnswered && inbound && <InboundCall key="inbound_call" />}
      {showQuestions && <Questions key="questions" />}
      {showScheduleCallback && <ScheduleCallback key="terminate" />}
      {showTerminate && <Terminate key="terminate" />}
      {showRating && <Rating key="rating" />}
    </div>
  }

  return (
    <div className="caller">
      {callStage === 'waiting_for_connection' && (
        <div>
          {pusherSubscribed && (
            <>
              {callingMode === 'desktop_web' && <StartCalling />}
              {callingMode === 'desktop_phone' && !onDemandCalling && <PhoneNumber />}
              {callingMode === 'desktop_phone' && onDemandCalling && <TargetList showDisconnect={true} />}
            </>
          )}
          {!pusherSubscribed && <div>Loading...</div>}
        </div>
      )}

      {callStage !== 'waiting_for_connection' && (
        <div>
          {!showTarget && getPreContent()}
        </div>
      )}


      {!(callStage === 'finished_call' && inputStage === 'redial') && ['finished_call', 'ringing_target', 'connected_to_target'].includes(callStage) && (
        <div className="columns">

          {showTargetInfo && (
            <div className="left-sidebar">
              {renderSidebarContent()}
            </div>
          )}

          {showScript && (
            <div ref={scrollRef} className={`caller-main${desktopSize ? ' box' : ''}`}>
              {showTarget && (
                <>
                  {showHangUpReminder && <HangUpReminder />}
                  <div className="script">{blocks()}</div>
                </>
              )}
            </div>
          )}

        </div>
      )}


    </div>
  )
}
